import React, { useRef, useState } from 'react';
import videoCallPreview from '../../../../../../assets/images/graphics/video_call_preview.png';

export const StopRecordingCard: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [redone, setRedone] = useState(false);

  const pad = (n: number) => String(n).padStart(2, '0');

  if (submitted) {
    return (
      <div className='w-full rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden text-left'>
        <div className='flex flex-col items-center justify-center py-10 gap-3 px-6'>
          <div className='w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center'>
            <svg
                className='w-6 h-6 text-emerald-600'
                fill='none'
                stroke='currentColor'
                strokeWidth={2.5}
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
          <p className='text-[13px] font-bold text-gray-800'>
            Response Submitted!
          </p>
          <p className='text-[11px] text-gray-400 text-center'>
            Your answer has been recorded and submitted successfully.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='w-full rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden text-left'>
      { /* ── Question header ── */ }
      <div className='px-4 pt-4 pb-2'>
        <div className='flex items-center gap-2 mb-2'>
          <span className='px-2.5 py-0.5 rounded-full border border-gray-200 text-[10px] font-medium text-gray-600 bg-white'>
            Question 1
          </span>
          <span className='px-2.5 py-0.5 rounded-full bg-green-100 text-[10px] font-semibold text-green-600'>
            Skill
          </span>
        </div>
        <p className='text-[12px] font-semibold text-gray-800 leading-snug'>
          Explain the role of React in frontend development as an AEM Developer.
        </p>
      </div>

      { /* ── Two-panel layout ── */ }
      <div
          className='grid grid-cols-2 gap-2 px-4 pb-3'
          style={{ gridTemplateColumns: '1fr 1fr' }}
      >
        { /* Left – Question reference panel */ }
        <div className='flex flex-col gap-1.5'>
          <div className='flex items-center gap-1.5 bg-violet-50 border border-violet-100 rounded-lg px-2 py-1'>
            <svg
                className='w-3 h-3 text-violet-400 flex-shrink-0'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
            <p className='text-[9px] font-bold text-violet-600 leading-none'>
              Question Reference
            </p>
          </div>

          <div
              className='bg-gray-50 border border-gray-100 rounded-xl p-2.5 flex flex-col gap-2 text-[9px] text-gray-600 leading-relaxed'
              style={{ aspectRatio: '4/3', overflow: 'hidden' }}
          >
            <div className='flex items-center gap-1'>
              <span className='px-1.5 py-0.5 rounded bg-violet-100 text-violet-600 font-bold uppercase tracking-wide text-[7px]'>
                Scenario
              </span>
            </div>
            <p className='text-gray-700 text-[9px] leading-snug'>
              You are a frontend developer joining a team that currently uses a
              monolithic jQuery codebase. The team is planning a migration to a
              modern component-based architecture.
            </p>
            <div className='flex flex-col gap-1 border-l-2 border-violet-300 pl-2'>
              { [
                'Project has 50,000+ lines of legacy JS',
                'Team of 6 developers, mixed experience',
                'No existing test suite',
              ].map((point, i) => (
                <div key={i} className='flex items-start gap-1'>
                  <span className='text-violet-400 font-bold mt-0.5'>·</span>
                  <span>{ point }</span>
                </div>
              )) }
            </div>
            <div className='mt-auto bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 flex items-start gap-1.5'>
              <svg
                  className='w-2.5 h-2.5 text-amber-500 flex-shrink-0 mt-0.5'
                  fill='currentColor'
                  viewBox='0 0 20 20'
              >
                <path
                    fillRule='evenodd'
                    d='M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z'
                    clipRule='evenodd'
                />
              </svg>
              <p className='text-[8px] text-amber-700 leading-tight'>
                Reference this scenario in your answer. No single correct answer
                exists.
              </p>
            </div>
          </div>
        </div>

        { /* Right – Recorded video playback + actions */ }
        <div className='flex flex-col gap-2'>
          { /* Video playback area */ }
          <div
              className='relative bg-black rounded-lg overflow-hidden'
              style={{ aspectRatio: '4/3' }}
          >
            <div className='absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-black'>
              <img
                  src={videoCallPreview}
                  alt='Professional Video Call Preview'
                  className='h-full w-auto opacity-70 object-contain'
              />
              { /* Subtle glow effect */ }
              <div className='absolute inset-0 bg-emerald-500/5 mix-blend-overlay pointer-events-none'></div>
            </div>
            { /* Overlay when no src — shows "Recorded" placeholder */ }
            <div className='absolute inset-0 flex flex-col items-center justify-center gap-1'>
              <div className='w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center backdrop-blur-sm border border-emerald-500/30'>
                <svg
                    className='w-5 h-5 text-emerald-400'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={2}
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <span className='text-[10px] text-emerald-400/80 font-bold tracking-wide uppercase'>
                Recording Securely Saved
              </span>
              <span className='text-[8px] text-white/40 tracking-tight'>
                Review is disabled for privacy
              </span>
            </div>
          </div>

          { /* Redo + Submit buttons */ }
          <div className='flex gap-1.5'>
            <button
                onClick={() => setRedone(true)}
                className={`flex-1 py-1.5 rounded-full text-[10px] font-semibold border transition-all duration-200 ${
                  redone
                    ? 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'
                }`}
                disabled={redone}
            >
              { redone ? '✓ Redone' : 'Redo Recording' }
            </button>
            <button
                onClick={() => setSubmitted(true)}
                className='flex-1 py-1.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg shadow-emerald-200 transition-all duration-200'
            >
              Submit Response
            </button>
          </div>
        </div>
      </div>

      { /* ── Bottom stats bar ── */ }
      <div className='flex items-stretch gap-2 px-4 pb-4'>
        { /* Timer — exhausted */ }
        <div className='flex-1 flex flex-col gap-1'>
          <div className='w-full h-1 bg-blue-500 rounded-full' />
          <div className='flex items-center gap-1.5'>
            <svg
                className='w-3.5 h-3.5 text-blue-400 flex-shrink-0'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                viewBox='0 0 24 24'
            >
              <circle cx='12' cy='12' r='9' />
              <path strokeLinecap='round' d='M12 7v5l3 3' />
            </svg>
            <span className='text-[14px] font-bold text-gray-800 tabular-nums'>
              { pad(0) }:{ pad(0) }
            </span>
          </div>
          <p className='text-[8px] uppercase tracking-widest text-gray-400'>
            Remaining Time
          </p>
        </div>

        { /* Skill questions */ }
        <div className='border border-blue-200 rounded-xl px-3 py-2 text-center min-w-[80px]'>
          <p className='text-[9px] font-semibold text-gray-500 uppercase tracking-wide leading-none mb-1'>
            Skill Questions
          </p>
          <p className='text-[18px] font-black text-gray-800 leading-none'>1</p>
          <p className='text-[8px] text-gray-400 uppercase tracking-wide'>
            Left to Answer
          </p>
        </div>

        { /* Video tips */ }
        <div className='flex flex-col gap-1 min-w-[90px]'>
          <div className='flex items-center gap-1 mb-0.5'>
            <svg
                className='w-3 h-3 text-emerald-500 flex-shrink-0'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z'
              />
            </svg>
            <p className='text-[9px] font-bold text-gray-700 uppercase tracking-wide'>
              Video Tips
            </p>
          </div>
          { [
            { text: 'Stable internet required' },
            { text: 'Quiet, well-lit environment' },
            { text: 'Re-recording allowed' },
          ].map((tip, i) => (
            <div key={i} className='flex items-start gap-1'>
              <span className='w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0 mt-0.5' />
              <p className='text-[9px] text-gray-500 leading-tight'>
                { tip.text }
              </p>
            </div>
          )) }
        </div>
      </div>
    </div>
  );
};
